import { useCallback, useEffect, useMemo, useState } from 'react';
import { presetFor, type BusinessType } from '@app/shared';
import { useAppApi } from '../../app/ApiProvider';
import type { CatalogCardDto } from '../../api/types';
import { groupCatalogByIndustry, industryName, industryOptions } from '../../domain/catalog';
import { cityDistrictLabel } from '../../domain/geo';
import { useAsync } from '../../hooks/useAsync';
import { useI18n } from '../../i18n/I18nProvider';
import { CityPickerSheet, type CitySelection } from '../../ui/CityPickerSheet';
import { Avatar, IndustryTile, LangSwitch } from '../../ui/controls';
import { ErrorState, Loader } from '../../ui/feedback';
import { ChevronDown, ICON_STROKE, INDUSTRY_ICON, MapPin, Search } from '../../ui/icons';
import { RatingBadge } from '../../ui/rating';
import { HomeButton, Screen, ScrollArea, SectionTitle } from '../../ui/Screen';

/** Пауза перед запросом поиска. Меньше — запрос на каждую букву, больше — заметная задержка. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Выбор города запоминается между запусками: повторный вход не должен снова спрашивать город.
 * localStorage, а не состояние домена — потеря выбора ничего не ломает, поэтому доступ обёрнут
 * в try/catch (приватный режим его роняет).
 */
const GEO_STORAGE_KEY = 'app.catalogCity';

function readStoredGeo(): CitySelection {
  try {
    const raw = window.localStorage.getItem(GEO_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<CitySelection>;
      return {
        city: typeof parsed.city === 'string' ? parsed.city : null,
        district: typeof parsed.district === 'string' ? parsed.district : null,
      };
    }
  } catch {
    // Приватный режим или битый JSON — молча откатываемся к «Все города».
  }
  return { city: null, district: null };
}

function writeStoredGeo(geo: CitySelection): void {
  try {
    window.localStorage.setItem(GEO_STORAGE_KEY, JSON.stringify(geo));
  } catch {
    // См. readStoredGeo: выбор просто не переживёт перезапуск.
  }
}

/**
 * Каталог — первый экран приложения (ТЗ §14.1). Заставки перед ним нет с 16 сентября 2026.
 *
 * Фильтр по отрасли и строка поиска уходят НА СЕРВЕР параметрами `?type=` и `?query=`,
 * а не применяются к уже загруженной странице: выдача постранична курсором, и фильтрация
 * «того, что успело приехать», показала бы клиенту пустоту при полном каталоге.
 *
 * Внутри страницы карточки разложены по отраслям (`groupCatalogByIndustry`). Порядок
 * отраслей задан пресетами `@app/shared` — см. комментарий там же.
 */
export function CatalogScreen(props: { onOpenBusiness: (slug: string) => void }): JSX.Element {
  const { t, lang } = useI18n();
  const api = useAppApi();

  const [type, setType] = useState<BusinessType | null>(null);
  // Набранное и отправленное — разные состояния: запрос на каждую букву положил бы
  // и сеть, и базу, а ждать окончания ввода некому.
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  // Выбранный город/район и открытие шторки выбора. Начальное значение — из localStorage.
  const [geo, setGeo] = useState<CitySelection>(() => readStoredGeo());
  const [geoOpen, setGeoOpen] = useState(false);
  // Накопленные страницы. Отдельным состоянием, потому что «показать ещё» дописывает
  // к списку, а смена фильтра его обнуляет — этих двух режимов у useAsync нет.
  const [extraPages, setExtraPages] = useState<readonly CatalogCardDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<unknown>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  const params = useMemo(
    () => ({
      ...(type ? { type } : {}),
      // Район уходит только вместе с городом: без города бэкенд его отвергает.
      ...(geo.city ? { city: geo.city } : {}),
      ...(geo.city && geo.district ? { district: geo.district } : {}),
      ...(query ? { query } : {}),
    }),
    [type, geo, query],
  );

  const applyGeo = (next: CitySelection): void => {
    setGeo(next);
    writeStoredGeo(next);
  };

  const firstPage = useAsync(
    useCallback(async () => {
      const page = await api.client.getCatalog(params);
      setExtraPages([]);
      setCursor(page.nextCursor);
      setLoadMoreError(null);
      return page;
    }, [api, params]),
    [params],
  );

  const items = useMemo(
    () => (firstPage.state.status === 'ready' ? [...firstPage.state.data.items, ...extraPages] : []),
    [firstPage.state, extraPages],
  );

  const sections = useMemo(() => groupCatalogByIndustry(items, lang), [items, lang]);

  const loadMore = async (): Promise<void> => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const page = await api.client.getCatalog({ ...params, cursor });
      setExtraPages((current) => [...current, ...page.items]);
      setCursor(page.nextCursor);
    } catch (error) {
      // Отказ дозагрузки не стирает уже показанное, но и не прячется: без сообщения
      // кнопка «показать ещё» выглядела бы просто неработающей.
      setLoadMoreError(error);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Screen>
      <ScrollArea bottomGap="tabs">
        <div className="flex items-start gap-3 px-4 pt-[calc(env(safe-area-inset-top,0px)+16px)]">
          <h1 className="min-w-0 flex-1 text-[26px] font-extrabold leading-[1.15] tracking-[-0.03em]">
            {t('catalogTitle')}
          </h1>
          <LangSwitch />
          <HomeButton />
        </div>
        <div className="px-4 pt-1.5 text-[13px] leading-[1.45] text-ink-3">{t('catalogSub')}</div>

        {/* Пилюля города: всегда на виду и явно тапабельна (иконка + стрелка), а выбранный
            город запоминается. «Все города» — когда фильтр не задан. */}
        <div className="px-4 pt-3">
          <button
            type="button"
            onClick={() => setGeoOpen(true)}
            className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-surface-2 px-3 py-2 text-[13px] font-semibold shadow-[inset_0_0_0_1px_var(--hair)]"
          >
            <MapPin size={15} strokeWidth={ICON_STROKE} className="shrink-0 text-accent" aria-hidden="true" />
            <span className="truncate">
              {cityDistrictLabel(geo.city, geo.district, lang) ?? t('geoAllCities')}
            </span>
            <ChevronDown size={15} strokeWidth={2} className="shrink-0 text-ink-3" aria-hidden="true" />
          </button>
        </div>

        <div className="px-4 pt-4">
          <label className="flex items-center gap-2.5 rounded-[13px] bg-surface-2 px-3.5 py-3">
            <Search size={18} strokeWidth={ICON_STROKE} className="shrink-0 text-ink-3" />
            <input
              type="search"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={t('catalogSearchPh')}
              className="min-w-0 flex-1 bg-transparent text-[14px] text-ink outline-none placeholder:text-ink-3"
            />
          </label>
        </div>

        {/* Плитки отраслей вместо прежних горизонтальных «таблеток» групп: отрасль — то,
            чем человек думает («мне к стоматологу»), а группа «Здоровье» требует от него
            догадаться, что стоматолог внутри неё. Повторное нажатие снимает фильтр:
            иначе вернуться ко всему каталогу было бы нечем. */}
        <div className="grid grid-cols-3 gap-2 px-4 pt-4">
          {industryOptions(lang).map((preset) => {
            const Icon = INDUSTRY_ICON[preset.type];
            const active = preset.type === type;
            return (
              <button
                key={preset.type}
                type="button"
                onClick={() => setType(active ? null : preset.type)}
                aria-pressed={active}
                className={`flex flex-col items-center gap-1.5 rounded-card px-2 py-3 text-center transition ${
                  active
                    ? 'bg-surface text-accent shadow-[inset_0_0_0_2px_var(--accent)]'
                    : 'bg-surface shadow-[inset_0_0_0_1px_var(--hair)]'
                }`}
              >
                <Icon size={24} strokeWidth={ICON_STROKE} aria-hidden="true" />
                <span className="clamp-2 text-[11.5px] font-semibold leading-[1.25]">
                  {industryName(preset, lang)}
                </span>
              </button>
            );
          })}
        </div>

        {firstPage.state.status === 'loading' ? <Loader /> : null}
        {firstPage.state.status === 'failed' ? (
          <ErrorState error={firstPage.state.error} onRetry={firstPage.reload} />
        ) : null}

        {firstPage.state.status === 'ready' && sections.length === 0 ? (
          <div className="px-4 pt-6 text-[13px] leading-[1.5] text-ink-3">{t('catalogEmpty')}</div>
        ) : null}

        {sections.map((section) => (
          <div key={section.type} className="px-4 pt-4">
            <SectionTitle>{industryName(section.preset, lang)}</SectionTitle>
            <div>
              {section.items.map((item, index) => (
                <div key={item.id}>
                  {index > 0 ? <div className="ml-[65px] h-px bg-hair" /> : null}
                  <CatalogRow item={item} onOpen={() => props.onOpenBusiness(item.slug)} />
                </div>
              ))}
            </div>
          </div>
        ))}

        {loadMoreError ? (
          <div className="px-4 pt-3">
            <ErrorState error={loadMoreError} />
          </div>
        ) : null}

        {cursor ? (
          <div className="px-4 pt-4">
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="w-full rounded-[11px] border border-dashed border-hair py-3 text-[13px] font-bold text-accent disabled:opacity-50"
            >
              {loadingMore ? t('loading') : t('loadMore')}
            </button>
          </div>
        ) : null}
      </ScrollArea>

      <CityPickerSheet
        open={geoOpen}
        value={geo}
        clearLabel={t('geoAllCities')}
        onClose={() => setGeoOpen(false)}
        onApply={applyGeo}
      />
    </Screen>
  );
}

function CatalogRow(props: { item: CatalogCardDto; onOpen: () => void }): JSX.Element {
  const { t, lang } = useI18n();
  // Отрасль обязана быть известна: `presetFor` бросает на чужой, и это осознанно —
  // см. groupCatalogByIndustry. Здесь берётся только иконка.
  const preset = presetFor(props.item.type);

  return (
    <button
      type="button"
      onClick={props.onOpen}
      className="flex w-full items-center gap-3 rounded-[15px] px-2.5 py-3 text-left transition active:bg-surface-2"
    >
      {/* Частный мастер рисуется человеком, заведение — плиткой отрасли: круглый аватар
          против квадрата различим с одного взгляда, не читая подписи. Ниже у мастера
          стоит отрасль («Репетиторы»), а не адрес: он у частника обычно не указан,
          а «Адрес не указан» под каждой карточкой — шум на весь список. */}
      {props.item.isSolo ? (
        <Avatar name={props.item.name} />
      ) : (
        <IndustryTile type={preset.type} />
      )}
      <div className="min-w-0 flex-1">
        <div className="clamp-2 text-[15.5px] font-bold tracking-tight">{props.item.name}</div>
        <RatingBadge sum={props.item.ratingSum} count={props.item.ratingCount} />
        <div className="mt-[3px] truncate text-[12.5px] text-ink-3">
          {props.item.isSolo ? industryName(preset, lang) : (props.item.address ?? t('addrNone'))}
        </div>
      </div>
      <span className="text-[18px] text-ink-3" aria-hidden="true">
        ›
      </span>
    </button>
  );
}
